import {createReducer, on} from "@ngrx/store";
import {AiChatActions} from "./ai-chat.actions";
import {AiChatMessage, AiChatState, generateUUID, initialAiChatState} from "./ai-chat.state";
import {sanitizeAiContent} from "../../utils/ai-content-sanitizer";

export const aiChatReducer = createReducer(
  initialAiChatState,

  // Panel
  on(AiChatActions.togglePanel, (state): AiChatState => ({
    ...state,
    isOpen: !state.isOpen,
  })),
  on(AiChatActions.openPanel, (state): AiChatState => ({
    ...state,
    isOpen: true,
  })),
  on(AiChatActions.closePanel, (state): AiChatState => ({
    ...state,
    isOpen: false,
  })),

  // New conversation — reset chat with new ID
  on(AiChatActions.newConversation, (state): AiChatState => ({
    ...initialAiChatState,
    conversationId: generateUUID(),
    isOpen: state.isOpen,
  })),

  // Send message — add user message, start streaming
  on(AiChatActions.sendMessage, (state, {message}): AiChatState => {
    const userMessage: AiChatMessage = {
      role: "user",
      content: message,
      timestamp: Date.now(),
    };
    return {
      ...state,
      messages: [...state.messages, userMessage],
      streamingText: "",
      streamingEntityReferences: [],
      streamingSuggestedActions: [],
      isStreaming: true,
      toolCallStatus: null,
      error: null,
    };
  }),

  // Stream chunk — append sanitized text
  on(AiChatActions.streamChunk, (state, {text}): AiChatState => ({
    ...state,
    streamingText: state.streamingText + sanitizeAiContent(text),
  })),

  // Tool call status
  on(AiChatActions.streamToolCall, (state, {tool, status}): AiChatState => ({
    ...state,
    toolCallStatus: {tool, status},
  })),

  // Stream complete — finalize assistant message with sanitized content and attached refs
  on(AiChatActions.streamComplete, (state): AiChatState => {
    const assistantMessage: AiChatMessage = {
      role: "assistant",
      content: sanitizeAiContent(state.streamingText),
      timestamp: Date.now(),
      entityReferences: state.streamingEntityReferences.length > 0 ? state.streamingEntityReferences : undefined,
      suggestedActions: state.streamingSuggestedActions.length > 0 ? state.streamingSuggestedActions : undefined,
    };
    return {
      ...state,
      messages: [...state.messages, assistantMessage],
      streamingText: "",
      streamingEntityReferences: [],
      streamingSuggestedActions: [],
      isStreaming: false,
      toolCallStatus: null,
    };
  }),

  // Entity references — accumulate in streaming state (attached to message on streamComplete)
  on(AiChatActions.streamEntityReferences, (state, {refs}): AiChatState => ({
    ...state,
    streamingEntityReferences: [...state.streamingEntityReferences, ...refs],
  })),

  // Suggested actions — store in streaming state (attached to message on streamComplete)
  on(AiChatActions.streamSuggestedActions, (state, {actions}): AiChatState => ({
    ...state,
    streamingSuggestedActions: actions,
  })),

  // Error — preserve partial response as assistant message
  on(AiChatActions.streamError, (state, {message}): AiChatState => {
    const messages = [...state.messages];
    if (state.streamingText.trim()) {
      messages.push({
        role: "assistant",
        content: sanitizeAiContent(state.streamingText),
        timestamp: Date.now(),
      });
    }
    return {
      ...state,
      messages,
      isStreaming: false,
      streamingText: "",
      streamingEntityReferences: [],
      streamingSuggestedActions: [],
      toolCallStatus: null,
      error: message,
    };
  }),
);
